import React, { useEffect, useRef } from 'react';
import cx from 'classnames';
import styles from './flipbook.module.scss';
import { FlipViewportAnimationSuppressContext } from './flipViewportAnimationSuppressContext';

const FLIP_TOTAL_MS  = 850;
const SLIDE_TOTAL_MS = 450;

// ── Slot context ──────────────────────────────────────────────────────────────
export const FlipSlotContext = React.createContext('idle');

// ── Fold slot class helper (desktop) ─────────────────────────────────────────
function foldSlotClass(slot, direction) {
  const map = {
    next: {
      currentA:  styles.flipNextCurrentA,
      currentB:  styles.flipNextCurrentB,
      upcomingA: styles.flipNextUpcomingA,
      upcomingB: styles.flipNextUpcomingB,
    },
    prev: {
      currentA:  styles.flipPrevCurrentA,
      currentB:  styles.flipPrevCurrentB,
      upcomingA: styles.flipPrevUpcomingA,
      upcomingB: styles.flipPrevUpcomingB,
    },
  };
  return map[direction]?.[slot] ?? '';
}

// ── Slide slot class helper (mobile) ─────────────────────────────────────────
function slideSlotClass(role, direction) {
  const map = {
    next: { current: styles.slideCurrentNext, upcoming: styles.slideUpcomingNext },
    prev: { current: styles.slideCurrentPrev, upcoming: styles.slideUpcomingPrev },
  };
  return map[direction]?.[role] ?? '';
}

// ── Component ─────────────────────────────────────────────────────────────────
export const FlipbookFlipContainer = ({
  currentContent,
  upcomingContent,
  direction,       // 'next' | 'prev' | null
  onAnimationEnd,
  mobile = false,
}) => {
  const timerRef = useRef(null);
  const onAnimationEndRef = useRef(onAnimationEnd);
  onAnimationEndRef.current = onAnimationEnd;

  useEffect(() => {
    if (!direction) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    const duration = mobile ? SLIDE_TOTAL_MS : FLIP_TOTAL_MS;
    timerRef.current = setTimeout(() => {
      onAnimationEndRef.current?.();
      timerRef.current = null;
    }, duration);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [direction, mobile]); // duration differs slide vs flip

  // Base slot: at idle shows the live current page; during a transition it shows
  // the frozen target snapshot under the animated layers. On mobile the slide
  // layers are fully visible on top; without this subtree in baseSlot, the page
  // would only mount in .slideSlot and then jump into baseSlot when idle —
  // remounting widgets causes a blink. Keep the same upcoming element here but
  // hide base visually during mobile slide only (desktop fold relies on clips).
  const baseContent = direction ? upcomingContent : currentContent;

  return (
    <FlipViewportAnimationSuppressContext.Provider value={!!direction}>
      <div className={cx(styles.flipViewport, { [styles.slideViewport]: mobile && direction })}>
        <FlipSlotContext.Provider value={direction ? 'upcoming' : 'idle'}>
          <div className={cx(styles.baseSlot, {
            [styles.baseSlotInert]: !!direction,
            [styles.baseSlotHiddenMobileSlide]: !!(mobile && direction),
          })}>
            {baseContent}
          </div>
        </FlipSlotContext.Provider>

        {direction && !mobile && (
          <>
            <FlipSlotContext.Provider value="upcoming">
              <div className={cx(styles.flipSlot, foldSlotClass('upcomingB', direction))}>
                {upcomingContent}
              </div>
              <div className={cx(styles.flipSlot, foldSlotClass('upcomingA', direction))}>
                {upcomingContent}
              </div>
            </FlipSlotContext.Provider>

            <FlipSlotContext.Provider value="current">
              <div className={cx(styles.flipSlot, foldSlotClass('currentA', direction))}>
                {currentContent}
              </div>
              <div className={cx(styles.flipSlot, foldSlotClass('currentB', direction))}>
                {currentContent}
              </div>
            </FlipSlotContext.Provider>
          </>
        )}

        {direction && mobile && (
          <>
            <FlipSlotContext.Provider value="current">
              <div className={cx(styles.slideSlot, slideSlotClass('current', direction))}>
                {currentContent}
              </div>
            </FlipSlotContext.Provider>
            <FlipSlotContext.Provider value="upcoming">
              <div className={cx(styles.slideSlot, slideSlotClass('upcoming', direction))}>
                {upcomingContent}
              </div>
            </FlipSlotContext.Provider>
          </>
        )}
      </div>
    </FlipViewportAnimationSuppressContext.Provider>
  );
};